import { useState } from 'react'
import { toast } from '@/components/ui/Toast'
import { api } from '@/lib/api'
import type { RegistrationRequest } from '@parchala/shared'

const ZONES = [
  'Circunvalar',
  'Centro',
  'Pinares',
  'Álamos',
  'Cerritos',
  'Cuba',
  'Dosquebradas',
  'La Victoria',
  'El Jardín',
  'Belmonte',
  'Santa Mónica',
  'El Poblado',
  'Kennedy',
  'Nacederos',
]
const CATEGORIES = [
  'Restaurante',
  'Bar / Rooftop',
  'Café',
  'Club / Discoteca',
  'Galería / Arte',
  'Centro de experiencias',
  'Espacio cultural',
  'Parque / Aire libre',
  'Cervecería',
  'Otro',
]

const C = { teal: '#0F5C5C', border: '#E1E5E3', ink: '#202523', ink2: '#6D7471' }
const FB = "'Inter', sans-serif"
const FH = "'Plus Jakarta Sans', sans-serif"

const fieldStyle: React.CSSProperties = { marginBottom: 14 }
const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 700,
  color: C.ink2,
  marginBottom: 6,
}
const inputStyle: React.CSSProperties = {
  width: '100%',
  border: `1px solid ${C.border}`,
  borderRadius: 11,
  padding: '12px 14px',
  fontFamily: FB,
  fontSize: 14,
  color: C.ink,
  outline: 'none',
}
const selectStyle: React.CSSProperties = { ...inputStyle, background: '#fff', cursor: 'pointer' }

interface Props {
  onSuccess?: () => void
}

export default function RegisterBusinessForm({ onSuccess }: Props) {
  const [form, setForm] = useState<Partial<RegistrationRequest>>({})
  const [loading, setLoading] = useState(false)

  const set =
    (field: keyof RegistrationRequest) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name || !form.category || !form.zone || !form.contact || !form.email) {
      toast.error('Completa todos los campos obligatorios')
      return
    }
    setLoading(true)
    try {
      // Guarda en localStorage (el admin lo revisa desde /admin/registrations)
      const pending = JSON.parse(localStorage.getItem('px_pending_biz') || '[]')
      pending.push({
        id: 'req_' + Date.now(),
        ...form,
        status: 'pending',
        sentAt: new Date().toISOString(),
      })
      localStorage.setItem('px_pending_biz', JSON.stringify(pending))

      // Si el API Worker está corriendo, también lo manda al backend
      try {
        await api.post('/api/auth/register-business', form)
      } catch {
        /* modo offline */
      }

      onSuccess?.()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al enviar')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={fieldStyle}>
        <label style={labelStyle}>Nombre del negocio *</label>
        <input
          style={inputStyle}
          type="text"
          placeholder="Ej: Cielo Rooftop"
          onChange={set('name')}
          required
          onFocus={(e) => (e.target.style.borderColor = C.teal)}
          onBlur={(e) => (e.target.style.borderColor = C.border)}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={fieldStyle}>
          <label style={labelStyle}>Categoría *</label>
          <select style={selectStyle} onChange={set('category')} defaultValue="" required>
            <option value="" disabled>
              Selecciona…
            </option>
            {CATEGORIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Zona *</label>
          <select style={selectStyle} onChange={set('zone')} defaultValue="" required>
            <option value="" disabled>
              Selecciona…
            </option>
            {ZONES.map((z) => (
              <option key={z}>{z}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Descripción breve</label>
        <textarea
          style={{ ...inputStyle, resize: 'none' }}
          rows={2}
          placeholder="¿Qué tipo de experiencia ofreces?"
          onChange={set('description')}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={fieldStyle}>
          <label style={labelStyle}>WhatsApp / Teléfono *</label>
          <input
            style={inputStyle}
            type="tel"
            placeholder="+507 6000-0000"
            onChange={set('contact')}
            required
            onFocus={(e) => (e.target.style.borderColor = C.teal)}
            onBlur={(e) => (e.target.style.borderColor = C.border)}
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Correo electrónico *</label>
          <input
            style={inputStyle}
            type="email"
            placeholder="negocio@ejemplo.com"
            onChange={set('email')}
            required
            onFocus={(e) => (e.target.style.borderColor = C.teal)}
            onBlur={(e) => (e.target.style.borderColor = C.border)}
          />
        </div>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Instagram (opcional)</label>
        <input
          style={inputStyle}
          type="text"
          placeholder="@tunegocio"
          onChange={set('instagram')}
          onFocus={(e) => (e.target.style.borderColor = C.teal)}
          onBlur={(e) => (e.target.style.borderColor = C.border)}
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        style={{
          width: '100%',
          padding: 14,
          borderRadius: 12,
          border: 'none',
          background: C.teal,
          color: '#fff',
          fontFamily: FH,
          fontWeight: 700,
          fontSize: 15,
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.7 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 7,
        }}
      >
        {loading ? 'Enviando…' : '📤 Enviar solicitud'}
      </button>
    </form>
  )
}
